import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import PasswordInput from '../PasswordInput';

const RegisterContainer = styled.div`
  max-width: 400px;
  width: 100%;
`;

const RegisterCard = styled.div`
  background: var(--white);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 30px;
`;

const RegisterHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const RegisterTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-primary);
`;

const RegisterSubtitle = styled.p`
  color: var(--color-text-tertiary);
  font-size: 14px;
`;

const Form = styled.form``;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  font-size: 14px;
  transition: border-color var(--transition-speed);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;


const SocialLoginDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--light-gray);
  }
  
  span {
    padding: 0 10px;
    color: var(--dark-gray);
    font-size: 14px;
  }
`;

const SocialLoginButton = styled.button<{ provider: 'google' | 'microsoft' }>`
  width: 100%;
  padding: 12px;
  background-color: ${props => props.provider === 'google' ? 'var(--color-primary)' : 'var(--color-primary-hover)'};
  color: var(--white);
  font-weight: 500;
  border-radius: var(--border-radius);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    margin-right: 10px;
  }
  
  &:hover {
    background-color: ${props => props.provider === 'google' ? 'var(--color-primary-hover)' : 'var(--color-success)'};
  }
`;

const ErrorMessage = styled.div`
  color: var(--danger-color);
  font-size: 14px;
  margin-top: 5px;
`;

const LoginPrompt = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  
  a {
    color: var(--primary-color);
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsCheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
`;

const TermsCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--primary-color);
  flex-shrink: 0;
`;

const TermsLabel = styled.label`
  font-size: 13px;
  color: var(--text-color);
  line-height: 1.4;
  cursor: pointer;
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsError = styled.div`
  color: var(--danger-color);
  font-size: 13px;
  margin-top: -8px;
  margin-bottom: 12px;
`;

const RegisterForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    terms?: string;
  }>({});
  
  const { register, googleLogin, microsoftLogin, loading } = useAuth();
  const navigate = useNavigate();
  
  const validateForm = (): boolean => {
    const errors: {
      name?: string;
      email?: string;
      password?: string;
      confirmPassword?: string;
      terms?: string;
    } = {};
    let isValid = true;
    
    if (!name) {
      errors.name = 'Name is required';
      isValid = false;
    }
    
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is not valid';
      isValid = false;
    }
    
    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter';
      isValid = false;
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'Password must contain at least one lowercase letter';
      isValid = false;
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'Password must contain at least one number';
      isValid = false;
    } else if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password)) {
      errors.password = 'Password must contain at least one special character';
      isValid = false;
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    
    if (!termsAccepted) {
      errors.terms = 'You must accept the Terms and Conditions and Privacy Policy to create an account';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError(null);
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const pendingSignup = await register(name, email, password);
      localStorage.setItem('pendingSignupEmail', pendingSignup.email);
      localStorage.setItem('pendingSignupId', pendingSignup.pendingId);
      navigate('/auth/verify-email');
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Could not create your account. Please try again.');
    }
  };
  
  const handleGoogleLogin = async () => {
    setRequestError(null);
    if (!termsAccepted) {
      setFormErrors({ terms: 'You must accept the Terms and Conditions and Privacy Policy to create an account' });
      return;
    }
    try {
      await googleLogin();
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Could not continue with Google. Please try again.');
    }
  };
  
  const handleMicrosoftLogin = async () => {
    setRequestError(null);
    if (!termsAccepted) {
      setFormErrors({ terms: 'You must accept the Terms and Conditions and Privacy Policy to create an account' });
      return;
    }
    try {
      await microsoftLogin();
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Could not continue with Microsoft. Please try again.');
    }
  };
  
  return (
    <RegisterContainer>
      <RegisterCard>
        {requestError && <ErrorMessage role="alert">{requestError}</ErrorMessage>}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Full Name</Label>
            <Input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
            />
            {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your work email"
            />
            <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', marginTop: '5px' }}>
              Use a work or organizational email to create an account.
            </div>
            {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              className=""
            />
            <PasswordStrengthIndicator password={password} />
            {formErrors.password && <ErrorMessage>{formErrors.password}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className=""
            />
            {formErrors.confirmPassword && <ErrorMessage>{formErrors.confirmPassword}</ErrorMessage>}
          </FormGroup>
          
          <TermsCheckboxContainer>
            <TermsCheckbox
              type="checkbox"
              id="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (e.target.checked && formErrors.terms) {
                  setFormErrors(prev => ({ ...prev, terms: undefined }));
                }
              }}
            />
            <TermsLabel htmlFor="termsAccepted">
              By signing up and using the services, you confirm that you have accepted our <a href="https://trust.evolveailabs.com/" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and have read our <a href="https://trust.evolveailabs.com/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </TermsLabel>
          </TermsCheckboxContainer>
          {formErrors.terms && <TermsError>{formErrors.terms}</TermsError>}
          
          <button type="submit" disabled={loading} className="btn btn-primary" style={{width: '100%', marginBottom: 16}}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </Form>
        
        <SocialLoginDivider>
          <span>OR</span>
        </SocialLoginDivider>
        
        <SocialLoginButton
          type="button"
          provider="google"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <i className="fab fa-google"></i>
          Continue with Google
        </SocialLoginButton>
        
        <SocialLoginButton
          type="button"
          provider="microsoft"
          onClick={handleMicrosoftLogin}
          disabled={loading}
        >
          <i className="fab fa-microsoft"></i>
          Continue with Microsoft
        </SocialLoginButton>
      </RegisterCard>
      
      <LoginPrompt>
        Already have an account? <Link to="/auth/login">Log in</Link>
      </LoginPrompt>
    </RegisterContainer>
  );
};

export default RegisterForm;
